import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';

interface Variable {
    name: string;
    description: string;
}

interface Variables {
    [category: string]: Variable[];
}

@Component({
    selector: 'app-add-template-dialog',
    templateUrl: './add-template-dialog.component.html',
    styleUrls: ['./add-template-dialog.component.sass'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatExpansionModule,
        MatTooltipModule,
        MatSnackBarModule,
        MatTabsModule,
        MatChipsModule
    ]
})
export class AddTemplateDialogComponent {
    form: FormGroup;
    loading = false;
    readOnly = false;
    templateTitle = 'Template';
    currentTab = 'editor';

    // Variáveis disponíveis para o template
    variables: Variables = {
        parceiro: [
            { name: '{{nome_parceiro}}', description: 'Nome do parceiro' },
            { name: '{{documento_parceiro}}', description: 'CNPJ/CPF do parceiro' },
            { name: '{{email_parceiro}}', description: 'Email do parceiro' },
            { name: '{{telefone_parceiro}}', description: 'Telefone do parceiro' },
            { name: '{{endereco_parceiro}}', description: 'Endereço completo do parceiro' },
            { name: '{{cidade_parceiro}}', description: 'Cidade do parceiro' },
            { name: '{{estado_parceiro}}', description: 'Estado do parceiro' },
            { name: '{{cep_parceiro}}', description: 'CEP do parceiro' }
        ],
        sistema: [
            { name: '{{sistema_parceiro}}', description: 'Nome do sistema' },
            { name: '{{tipo_sistema}}', description: 'Tipo do sistema (Principal/Módulo)' },
            { name: '{{email_sistema}}', description: 'Email de contato do sistema' },
            { name: '{{descricao_sistema}}', description: 'Descrição do sistema' },
            { name: '{{vencimento_sistema}}', description: 'Dia de vencimento do sistema' }
        ],
        datas: [
            { name: '{{data_inicio}}', description: 'Data de início do contrato' },
            { name: '{{data_termino}}', description: 'Data de término do contrato' }
        ]
    };

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<AddTemplateDialogComponent>,
        private snackBar: MatSnackBar,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.form = this.fb.group({
            template: [data?.template || '']
        });

        // Definir se é somente leitura
        this.readOnly = data?.readOnly || false;

        // Pegar o título do template (Consultor, Parceiro, etc)
        this.templateTitle = data?.templateTitle || 'Template';

        // Se for modo somente leitura, desabilitar o formulário
        if (this.readOnly) {
            this.form.disable();
        }
    }

    getVariableCategories(): string[] {
        return Object.keys(this.variables);
    }

    getCategoryTitle(category: string): string {
        switch (category) {
            case 'parceiro': return 'Parceiro';
            case 'sistema': return 'Sistema';
            case 'datas': return 'Datas';
            default: return category.charAt(0).toUpperCase() + category.slice(1);
        }
    }

    insertVariable(variable: string): void {
        if (this.readOnly) return;

        const textArea = this.form.get('template');
        if (textArea) {
            const currentValue = textArea.value || '';
            const cursorPosition = document.querySelector('textarea')?.selectionStart || currentValue.length;

            // Inserir a variável na posição do cursor
            const newValue = currentValue.substring(0, cursorPosition) +
                variable +
                currentValue.substring(cursorPosition);

            textArea.setValue(newValue);
            textArea.markAsDirty();

            // Feedback visual
            this.snackBar.open(`Variável ${variable} inserida`, 'OK', {
                duration: 2000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom'
            });

            // Foco no textarea após inserir
            setTimeout(() => {
                const textarea = document.querySelector('textarea');
                if (textarea) {
                    textarea.focus();
                    // Posicionar o cursor após a variável inserida
                    const newPosition = cursorPosition + variable.length;
                    textarea.setSelectionRange(newPosition, newPosition);
                }
            }, 100);
        }
    }

    setTab(tab: string): void {
        this.currentTab = tab;
    }

    formatTemplate(): void {
        const textArea = this.form.get('template');
        if (textArea) {
            let content = textArea.value || '';

            // Formatação básica de HTML
            content = content
                .replace(/>\s+</g, '>\n<') // Quebrar linha entre tags
                .replace(/<div/g, '\n<div') // Quebrar linha antes de divs
                .replace(/<\/div>/g, '</div>\n') // Quebrar linha após divs
                .replace(/<p/g, '\n<p') // Quebrar linha antes de parágrafos
                .replace(/<\/p>/g, '</p>\n') // Quebrar linha após parágrafos
                .replace(/<table/g, '\n<table') // Quebrar linha antes de tabelas
                .replace(/<\/table>/g, '</table>\n') // Quebrar linha após tabelas
                .replace(/<tr/g, '\n  <tr') // Identar linhas de tabela
                .replace(/<td/g, '\n    <td') // Identar células de tabela
                .replace(/\n\s*\n/g, '\n'); // Remover linhas vazias extras

            textArea.setValue(content);
            textArea.markAsDirty();

            this.snackBar.open('Template formatado', 'OK', {
                duration: 2000
            });
        }
    }

    onSave(): void {
        const templateValue = this.form.get('template')?.value;
        if (templateValue) {
            this.dialogRef.close({
                content: templateValue,
                type: this.data?.type
            });
        }
    }

    onCancel(): void {
        this.dialogRef.close();
    }
} 